import React, { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBan, faCalendarPlus, faCheck, faPlay, faTrash } from '@fortawesome/free-solid-svg-icons';
import { ListColumnType, ListFilterType, ListRowType } from 'types/ListTypes';
import AbstractList from 'components/lists/AbstractList';
import ListContext from 'components/lists/ListContext';
import { renderDateTime } from 'components/lists/functions/renderListColumn';
import { useModal } from 'components/modals/ModalContext';
import { saveData } from 'services/api/saveData';
import { useSettings } from 'services/settings/SettingsContext';
import EnvironmentDeleteModal from './EnvironmentDeleteModal';

/*
 * TrialList.tsx
 * Superuser and staff list of the trials. A running trial can be
 * extended or suspended, a superuser can delete the data of a trial
 * from the running list and from the archive as long as its
 * environment still holds data. The trial row itself is always kept
 * so the archive shows who created which trial and when.
 */

// Icon which suspends or reactivates the environment of a trial
const TrialAccessToggle: React.FC<{ environmentId: number, suspended: boolean }> = ({ environmentId, suspended }) => {
    const { t } = useTranslation();
    const [isSuspended, setIsSuspended] = useState<boolean>(suspended);
    const [loading, setLoading] = useState<boolean>(false);

    // Toggle the access of the trial environment
    const toggleAccess = async (event: React.MouseEvent) => {
        // Prevent opening the row click handler of the list
        event.stopPropagation();
        setLoading(true);
        try {
            const response = await saveData({
                apiUrl: `patch_environment_access_as_superuser/${environmentId}`,
                method: 'patch',
                data: { action: isSuspended ? 'reactivate' : 'suspend' },
            });
            if (response) {
                setIsSuspended(!isSuspended);
            }
        } catch (error) {
            console.error('Toggling trial access failed', error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className={`trial-access-toggle tooltip-icon ${isSuspended ? 'suspended' : ''} ${loading ? 'disabled' : ''}`}
             onClick={toggleAccess}>
            <FontAwesomeIcon icon={isSuspended ? faPlay : faBan} />
            <span className='tooltip'>
                {isSuspended
                    ? t('trial.general.reactivate_access_tooltip')
                    : t('trial.general.suspend_access_tooltip')}
            </span>
        </div>
    );
};

// Icon which extends the trial period of a trial with 7 days
const TrialExtendButton: React.FC<{ environmentId: number }> = ({ environmentId }) => {
    const { t } = useTranslation();
    const [loading, setLoading] = useState<boolean>(false);
    const [result, setResult] = useState<'extended' | 'failed' | null>(null);

    // Extend the trial period of the environment
    const extendTrial = async (event: React.MouseEvent) => {
        // Prevent opening the row click handler of the list
        event.stopPropagation();
        setLoading(true);
        try {
            const response = await saveData({
                apiUrl: `patch_trial_extension_as_superuser/${environmentId}`,
                method: 'patch',
                data: {},
            });
            if (response) {
                setResult('extended');
            }
        } catch (error) {
            console.error('Extending trial failed', error);
            setResult('failed');
        } finally {
            setLoading(false);
        }
    };

    // Show a check icon after a successful extension
    if (result === 'extended') {
        return (
            <div className='trial-extend-toggle extended tooltip-icon'>
                <FontAwesomeIcon icon={faCheck} />
                <span className='tooltip'>{t('trial.general.trial_extended_label')}</span>
            </div>
        );
    }

    return (
        <div className={`trial-extend-toggle tooltip-icon ${result === 'failed' ? 'failed' : ''} ${loading ? 'disabled' : ''}`}
             onClick={extendTrial}>
            <FontAwesomeIcon icon={faCalendarPlus} />
            <span className='tooltip'>
                {result === 'failed'
                    ? t('trial.general.trial_extension_failed_label')
                    : t('trial.general.extend_trial_tooltip')}
            </span>
        </div>
    );
};

// Icon which opens the deletion modal of the trial environment
const TrialDeleteIcon: React.FC<{ row: ListRowType }> = ({ row }) => {
    const { t } = useTranslation();
    const { initializeModal } = useModal();
    const { refetchList } = useContext(ListContext);

    // Only trials the backend marks as deletable get the delete icon
    if (!row.is_deletable) return null;

    // Open the deletion modal with the trial endpoints, without triggering the row click
    const openDeleteModal = (event: React.MouseEvent) => {
        event.stopPropagation();
        initializeModal(
            <EnvironmentDeleteModal
                environmentId={row.id}
                environmentName={row.environment_name as string}
                onFinished={refetchList}
                deleteEndpoint='delete_trial_as_superuser'
                statusEndpoint='get_trial_deletion_status_as_superuser'
            />,
            { modalSize: 'extra-small' }
        );
    };

    return (
        <div className='list-delete-icon tooltip-icon environment-delete-icon'>
            <FontAwesomeIcon
                icon={faTrash}
                className='fas fa-trash'
                style={{ cursor: 'pointer' }}
                onClick={openDeleteModal} />
            <span className='tooltip'>{t('trial.general.delete_tooltip')}</span>
        </div>
    );
};

// Rename the name of the function:
const TrialList: React.FC = () => {
    const { t } = useTranslation();
    const { userLocale } = useSettings();

    // Specify the options of this list:
    const query = {
        endpoint: 'get_trial_as_superuser_list',
        defaultParams: { 'is_active': true },
        object: 'trial_as_superuser'
    }
    const objectName = 'trial.general.object_name'
    const showSearch = true

    // Set isPaginated to true if this list may have 100.000+ items:
    const isPaginated = true

    // A trial whose environment is being deleted or is gone has no data to act on
    const hasData = (row: ListRowType) => {
        const deletionStatus = row.environment_deletion_status as string | undefined;
        return Boolean(row.environment_id) && deletionStatus !== 'deleting' && deletionStatus !== 'deleted';
    };

    // Render whether the data of the trial environment is still present
    const renderDataStatus = (row: ListRowType) => {
        const deletionStatus = (row.environment_deletion_status as string | undefined) ?? 'deleted';
        const statusKey = deletionStatus === 'deleting' || deletionStatus === 'deleted' ? deletionStatus : 'present';
        return <span className={`trial-data-status ${statusKey}`}>{t(`trial.data_status.${statusKey}`)}</span>;
    };

    // Specify the columns of this list. The text columns use minmax so the
    // header and the rows resolve the tracks identically, the button columns
    // get a fixed width, otherwise long content misaligns the columns
    const columns: ListColumnType[] = [
        { width: 'minmax(0, 2fr)', field: 'start_date', label: 'trial.general.start_date_label', render: (_, row) => renderDateTime(row.start_date, userLocale) },
        { width: 'minmax(0, 2fr)', field: 'end_date', label: 'trial.general.end_date_label', render: (_, row) => renderDateTime(row.end_date, userLocale) },
        { width: 'minmax(0, 3fr)', field: 'company_name', label: 'trial.general.company_name_label', customClass: 'truncate-cell' },
        { width: 'minmax(0, 3fr)', field: 'main_user', label: 'trial.general.main_user_label', customClass: 'truncate-cell' },
        { width: 'minmax(0, 1.5fr)', field: 'environment_deletion_status', label: 'trial.general.data_label', disableOrdering: true, render: (_, row) => renderDataStatus(row) },
        {
            width: '2.5rem',
            field: 'trial_extension',
            label: '',
            customClass: 'icon-action-column',
            render: (_, row) => (
                hasData(row) && row.is_active
                    ? <TrialExtendButton environmentId={row.environment_id as number} />
                    : <></>
            )
        },
        {
            width: '2.5rem',
            field: 'environment_suspended',
            label: '',
            customClass: 'icon-action-column',
            render: (_, row) => (
                // Only running trials can be suspended, an expired trial has no access anyway
                hasData(row) && row.end_date && new Date(row.end_date as string) > new Date()
                    ? <TrialAccessToggle environmentId={row.environment_id as number} suspended={Boolean(row.environment_suspended)} />
                    : <></>
            )
        },
        { width: '2rem', field: 'is_deletable', label: '', disableOrdering: true, render: (_, row) => <TrialDeleteIcon row={row} /> },
    ];

    // Specify the filters of this list:
    const filters: ListFilterType[] = [
        {
            id: 'active',
            label: 'trial.general.active_trials_filter_label',
            params: { 'is_active': true },
            default: true,
        },
        {
            group: 'general.archive',
            filters: [
                {
                    id: 'deactivated',
                    label: 'trial.general.trial_archive_filter_label',
                    shortName: 'general.deactivated',
                    params: { 'is_active': false },
                }
            ]
        }
    ]

    return (
        <AbstractList
            query={query}
            objectName={objectName}
            filters={filters}
            columns={columns}
            showAddButton={false}
            showSearch={showSearch}
            isPaginated={isPaginated}
        />
    );
};

export default TrialList;
